"use server"

import { sdk } from "@lib/config"
import { getAuthHeaders } from "@lib/data/cookies"
import {
  parseBonusFormFields,
  type BonusFormField,
  type BonusFormValues,
} from "@modules/bonus/lib/bonus-form-model"

// Story 8.1 — a camada de dados do Bónus. `"use server"`: o browser NUNCA fala com o Engine direto (o
// SDK é server-side e anexa a publishable key sozinho — nunca a pôr à mão). Resultado DISCRIMINADO
// (`{ok:true}|{ok:false,error}`) porque um server action NUNCA lança ao componente: o formulário tem de
// mostrar um erro, não desaparecer num boundary.
//
// A rota `/store/bonus-requests` é PÚBLICA (não registada em `middlewares.ts`), logo nada aqui exige
// sessão. Ainda assim ANEXAMOS `getAuthHeaders()`: para um guest devolve `{}` (é o caso normal), e para
// um cliente com sessão leva o bearer que faz o Engine gravar `customer_id = actor_id` (AC4). Sem isto,
// o `customer_id` nunca seria preenchido a partir do produto e a metade autenticada do AC4 só existiria
// via chamadas diretas à API. O header é opcional — nunca um requisito.

const BONUS_FORM_PATH = "/store/bonus-form"
const BONUS_REQUESTS_PATH = "/store/bonus-requests"

const HTTP_BAD_REQUEST = 400

const FIELDS_ERROR_MESSAGE =
  "Não foi possível carregar o formulário agora. Atualize a página em instantes."
const SUBMIT_ERROR_MESSAGE =
  "Não foi possível registrar sua solicitação agora. Tente novamente em instantes."

export type BonusFormFieldsResult =
  | { ok: true; fields: BonusFormField[] }
  | { ok: false; error: string }

export type SubmitBonusRequestResult = { ok: true; id: string } | { ok: false; error: string }

/**
 * Lê a definição dos campos do Engine. É o que torna a página `/bonus` uma renderização DA
 * CONFIGURAÇÃO (AC3) — os campos não estão escritos no JSX.
 *
 * `no-store`: a definição é servida de uma constante do Engine e muda com um deploy, não com o tempo;
 * cachear aqui só criaria um estado em que a página valida por uma definição e o servidor por outra.
 */
export const getBonusFormFields = async (): Promise<BonusFormFieldsResult> => {
  try {
    const data = await sdk.client.fetch<unknown>(BONUS_FORM_PATH, {
      method: "GET",
      cache: "no-store",
    })
    const fields = parseBonusFormFields(data)
    if (fields === null) {
      // Payload fora do contrato ⇒ erro honesto, nunca um formulário meio-formado.
      console.error("[bonus] GET /store/bonus-form devolveu um payload fora do contrato:", data)
      return { ok: false, error: FIELDS_ERROR_MESSAGE }
    }
    return { ok: true, fields }
  } catch (error) {
    // Erro NUNCA silenciado (regra do projeto): loga a causa antes de degradar.
    console.error("[bonus] falha ao ler a definição do formulário:", error)
    return { ok: false, error: FIELDS_ERROR_MESSAGE }
  }
}

/**
 * Submete a solicitação. `values` já vem aparado e sem chaves fora da definição
 * (`toBonusSubmission`) — o Engine tem schema FECHADO e recusaria o resto com 400.
 *
 * NUNCA envia `customer_id`: o vínculo com o cliente é resolvido pelo Engine a partir do token, e
 * mandá-lo no corpo daria 400 (é o que fecha a injeção).
 */
export const submitBonusRequest = async (
  values: BonusFormValues,
): Promise<SubmitBonusRequestResult> => {
  const headers = { ...(await getAuthHeaders()) }
  try {
    const data = await sdk.client.fetch<{ id?: unknown }>(BONUS_REQUESTS_PATH, {
      method: "POST",
      body: values,
      headers,
      cache: "no-store",
    })
    if (typeof data?.id !== "string") {
      console.error("[bonus] POST /store/bonus-requests devolveu uma resposta sem id:", data)
      return { ok: false, error: SUBMIT_ERROR_MESSAGE }
    }
    return { ok: true, id: data.id }
  } catch (error) {
    // O SDK lança `FetchError` (com `.status`) em não-2xx. Num 400, o `.message` é a mensagem PT-BR do
    // validador AUTORITÁRIO do Engine — mostramo-la tal e qual, porque diz exatamente qual campo
    // recusou. Só o resto (5xx/rede) vira a mensagem genérica.
    //
    // Isto DEPENDE de a rota devolver a mensagem na chave `message`: o SDK faz
    // `new FetchError(jsonError.message ?? resp.statusText, …)`, logo um corpo só com `{error}` chegaria
    // aqui como `"Bad Request"` — inglês, e sem dizer que campo falhou. A rota manda `error` E `message`
    // de propósito (ver `apps/backend/src/api/store/bonus-requests/route.ts`); se um dia alguém tirar o
    // `message` de lá, é AQUI que o utilizador paga, e em silêncio.
    const status = (error as { status?: number }).status
    if (status === HTTP_BAD_REQUEST) {
      const message = (error as { message?: unknown }).message
      return {
        ok: false,
        error: typeof message === "string" && message.length > 0 ? message : SUBMIT_ERROR_MESSAGE,
      }
    }
    console.error("[bonus] falha ao submeter a solicitação de bónus:", error)
    return { ok: false, error: SUBMIT_ERROR_MESSAGE }
  }
}
